import { ReactElement, useEffect, useRef } from 'react';
import { observer } from 'mobx-react-lite';
import { useCAWContext } from '../CAW';
import classnames from 'classnames';
import { translate } from '@micro-apps/env';
import Icon, { IconClose } from '../../icon';
import classNames from 'classnames';

interface Props {
	wrapperClassName?: string;
	inputClassName?: string;
	errorPromoCode?: boolean;
	autoFocus?: boolean;
}

function Promo({
	wrapperClassName,
	inputClassName,
	errorPromoCode,
	autoFocus = false,
}: Props): ReactElement<any> {
	const { planStore, isSingleProperty, isPPC, isDesktop } = useCAWContext();
	const inputRef = useRef<HTMLInputElement>(null);

	// Function to handle the event
	function handleCawComponentOpen(event: any) {
		if (event?.detail?.setFocusTo === 'promoCode') {
			inputRef?.current?.focus();
		}
	}

	useEffect(() => {
		if (autoFocus) {
			inputRef.current?.focus();
		}
	}, [autoFocus]);

	useEffect(() => {
		// Adding the event listener to the document
		window.addEventListener('cawComponentOpen', handleCawComponentOpen);
		return () => {
			// Cleaning up the event listener
			window.removeEventListener('cawComponentOpen', handleCawComponentOpen);
		};
	}, []);
	return (
		<div
			className={classnames(
				'togglePromoWrapper',
				{
					'flex w-full px-0 pt-[32px]': isPPC,
					'w-[150px]': !isPPC,
				},
				wrapperClassName
			)}
		>
			<div
				className={classnames('PromoCode-container', {
					'singleProperty w-full pr-[0px]': isSingleProperty,
					'col-lg-12 min-w-[30px] w-full flex-[1_1_100%] self-end pr-0 px-0': isPPC,
					'w-full': !isPPC && !isSingleProperty,
					col: !isPPC,
				})}
			>
				<div className="w-full flex flex-col spacing-stack-2xs">
					{isDesktop && (
						<label
							className={classnames('m-0 opacity-100 text-label', 'static')}
							htmlFor="promoCode"
						>
							<span className="text-text-secondary">{translate('Promo')}</span>
						</label>
					)}
					<div className="relative w-full">
						<input
							type="text"
							className={classNames(
								'h-10 min-h-10 w-full border-border-subtle border-[1px] rounded-[4px] px-[10px] py-[6px] tracking-[1px] text-body3 text-text-primary text-[16px]',
								{
									'bg-white disabled:bg-[#d9d9d9]': isPPC,
									'bg-surface-form': !isPPC,
									'PromoCode-error border-error-border border-[2px]': errorPromoCode,
									'pr-12': !!planStore?.search.promoCode,
								},
								inputClassName
							)}
							placeholder={isDesktop ? translate('Promo Code') : translate('Enter Promo Code')}
							id="promoCode"
							value={planStore?.search.promoCode}
							onChange={e => {
								planStore?.updateSearch({
									promoCode: e.target.value,
								});
							}}
							disabled={!!planStore?.search.flexDatesSelected}
							ref={inputRef}
						/>
						{planStore?.search.promoCode ? (
							<button
								type="button"
								className="textInput_clear absolute !right-[15px] top-1/2 z-[1] flex -translate-y-1/2 items-center justify-center !opacity-100 text-text-secondary transition-none hover:text-text-primary focus:outline-none focus-visible:ring-1 focus-visible:ring-border-strong"
								aria-label={translate('Clear')}
								title={translate('Clear')}
								onClick={() => {
									planStore?.updateSearch({
										promoCode: '',
									});
									inputRef.current?.focus();
								}}
							>
								<IconClose width={25} height={25} fill="currentColor" />
							</button>
						) : undefined}
					</div>
				</div>
				{(planStore?.search?.flexDatesSelected || 0) > 0 ? (
					<div className="PromoCode-warning-container">
						{isDesktop && !isPPC && (
							<span
								className={classnames('up-pointer', {
									'promo-set': !!planStore?.search.promoCode,
								})}
							></span>
						)}

						<div
							className={classnames(
								'PromoCode-warning ty-b3 flex gap-[10px] px-[10px] pt-[10px] pb-[10px]',
								{
									'border border-warning-border bg-warning-container-subtle text-info-text': !isPPC,
									'border-none bg-transparent px-[10px] py-[5px] text-text-primary text-[13px] leading-[25px] tracking-[0.5px]':
										isPPC,
									'promo-set': !!planStore?.search.promoCode,
								}
							)}
						>
							<Icon icon="information"></Icon>
							{translate('A promo code cannot be applied with a flexible date search.')}
						</div>
					</div>
				) : (
					<div className={classnames('promoWarningWrapper', isPPC && 'h-[35px]')}></div>
				)}
			</div>
		</div>
	);
}

export default observer(Promo);
